import { Box, Slider, Stack, TextField, Typography } from "@mui/material";
import set from "lodash/set";
import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { MoneyFlowRequestDto } from "@/shared/api";

type RangeFilterProps = {
  name: string;
  label?: string;
  minValue: unknown;
  maxValue: unknown;
  minError?: string | null;
  maxError?: string | null;
  setState?: Dispatch<SetStateAction<MoneyFlowRequestDto>>;
  onChange?: (param: [number, number]) => void;
  step?: number;
  max?: number;
  min?: number;
};

export const RangeFilter = ({
  name,
  label,
  minValue,
  maxValue,
  minError,
  maxError,
  setState,
  step,
  max,
  min,
  onChange,
}: RangeFilterProps) => {
  const { t } = useTranslation();

  const minValueNumber = minValue ? Number(minValue) : 0;
  const maxValueNumber = maxValue ? Number(maxValue) : 0;

  return (
    <Stack spacing={1}>
      {!!label && <Typography sx={{ fontSize: "14px" }}>{label}</Typography>}
      <Slider
        aria-label={label}
        defaultValue={0}
        step={step}
        value={[minValueNumber, maxValueNumber]}
        valueLabelDisplay="auto"
        onChange={(_e, newValue) => {
          const range = newValue as number[];
          if (setState) {
            setState((prevState) => {
              const newState = { ...prevState };
              set(newState, `${name}.gte`, range[0]);
              set(newState, `${name}.lte`, range[1]);
              return newState;
            });
          }
          onChange?.([range[0], range[1]]);
        }}
        min={min}
        max={max}
        disableSwap
      />
      <Box display="flex" gap={2}>
        <TextField
          label={t("min")}
          type="number"
          size="small"
          fullWidth
          value={minValue !== undefined ? minValue : ""}
          onChange={(e) => {
            let newValue = e.target.value === "" ? undefined : Number(e.target.value);
            if (typeof newValue === "number" && newValue < 0) {
              newValue = 0;
            }
            if (setState) {
              setState((prevState) => {
                const newState = { ...prevState };
                set(newState, `${name}.gte`, newValue);

                return newState;
              });
            }
            onChange?.([Number(newValue), maxValueNumber]);
          }}
          onBlur={(e) => {
            const value = e.target.value;
            let newValue = value === "" ? 0 : Number(value);
            if (typeof maxValue === "number" && Number(newValue) > maxValue) {
              newValue = maxValue;
            }
            if (min && newValue < min) {
              newValue = min;
            }

            if (max && newValue > max) {
              newValue = max;
            }

            if (setState) {
              setState((prevState) => {
                const newState = { ...prevState };
                set(newState, `${name}.gte`, newValue);

                return newState;
              });
            }
            onChange?.([newValue, Number(maxValue)]);
          }}
          error={!!minError}
        />
        <TextField
          label={t("max")}
          type="number"
          size="small"
          fullWidth
          value={maxValue !== undefined ? maxValue : ""}
          onChange={(e) => {
            let newValue = e.target.value === "" ? undefined : Number(e.target.value);
            if (typeof newValue === "number" && newValue < 0) {
              newValue = 0;
            }
            if (setState) {
              setState((prevState) => {
                const newState = { ...prevState };
                set(newState, `${name}.lte`, newValue);

                return newState;
              });
            }
            onChange?.([Number(minValue), Number(newValue)]);
          }}
          onBlur={(e) => {
            const value = e.target.value;
            let newValue = value === "" ? 0 : Number(value);
            if (typeof minValue === "number" && Number(newValue) < minValue) {
              newValue = minValue;
            }
            if (max && newValue > max) {
              newValue = max;
            }
            if (setState) {
              setState((prevState) => {
                const newState = { ...prevState };
                set(newState, `${name}.lte`, newValue);

                return newState;
              });
            }
            onChange?.([Number(minValue), Number(newValue)]);
          }}
          error={!!maxError}
        />
      </Box>
    </Stack>
  );
};
